import React from 'react';

import { MDText } from 'i18n-react';

import { SERVICE_FEES_ROUTE } from 'Utilities/routing';

import { ComponentContent, CONTENT_SIZES } from 'Layout';

import { useMetaTags } from 'Hooks/misc/useMetaTags';

import { FeesTable } from 'Components/transactionFees/FeesTable/FeesTable';

import sharedTexts from '../../../../../texts.json';
import { RoundedSection } from '../../../common/RoundedSection/RoundedSection';
import More from '../../../contentPages/aboutUs/More';
import { ExpandableContentWrapper } from '../../Components/ExpandableContentWrapper/ExpandableContentWrapper';
import { Trustpilot } from '../../Components/Trustpilot/Trustpilot';

import Texts from './ServiceFees.texts.json';

import styles from './ServiceFees.scss';

const T = new MDText(Texts);
const SharedT = new MDText(sharedTexts);

const t = (key: string) => T.translate(key) as string;
const sharedT = (key: string) => SharedT.translate(key) as string;

type ContentCardProps = {
  children?: React.ReactNode;
  listItems?: string[];
  paragraphs?: string[];
  title: string;
};

const ContentCard: React.FC<ContentCardProps> = ({ children, listItems, paragraphs, title }) => (
  <article className={styles.contentCard}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    {paragraphs && paragraphs.length > 0 && (
      <div className={styles.sectionBody}>
        {paragraphs.map((paragraph, index) => (
          <p key={`${title}-${index}`}>{paragraph}</p>
        ))}
      </div>
    )}
    {listItems && listItems.length > 0 && (
      <ul className={styles.list}>
        {listItems.map((item, index) => (
          <li key={`${title}-list-${index}`}>{item}</li>
        ))}
      </ul>
    )}
    {children}
  </article>
);

const ServiceFees: React.FC = () => {
  useMetaTags({
    description: sharedT('serviceFeesView.metaDescription'),
    title: sharedT('serviceFeesView.metaTitle'),
  }, SERVICE_FEES_ROUTE);

  return (
    <div className={styles.component}>
      <section className={styles.pageTitleSection}>
        <ComponentContent maxWidth={CONTENT_SIZES.large}>
          <h1 className={styles.pageTitle}>{t('pageTitle')}</h1>
        </ComponentContent>
      </section>

      <div className={styles.contentSections}>
        <ContentCard
          paragraphs={[t('howItWorks.p1'), t('howItWorks.p2'), t('howItWorks.p3')]}
          title={t('howItWorks.title')}
        />

        <ContentCard
          listItems={Texts.whatItCovers.ul.map((_, index) => t(`whatItCovers.ul.${index}`))}
          paragraphs={[t('whatItCovers.p1')]}
          title={t('whatItCovers.title')}
        />

        <ContentCard
          paragraphs={[t('howMuch.description')]}
          title={t('howMuch.title')}
        >
          <div className={styles.feesTableWrapper}>
            <FeesTable />
          </div>
        </ContentCard>

        <ContentCard
          paragraphs={[t('howPaid.p1'), t('howPaid.p2')]}
          title={t('howPaid.title')}
        />
      </div>

      <RoundedSection withBottomLedge withTopLedge>
        <ExpandableContentWrapper
          faq={Texts.faq.items.map((_, index) => ({
            answer: t(`faq.items.${index}.answer`),
            question: t(`faq.items.${index}.question`),
          }))}
          faqTitle={t('faq.title')}
        />
      </RoundedSection>

      <RoundedSection withBottomLedge withTopLedge className={styles.roundedSection} theme='primary-yellow'>
        <More />
      </RoundedSection>

      <RoundedSection withTopLedge className={styles.roundedSection}>
        <Trustpilot wrapperClassName={styles.trustpilotSection} />
      </RoundedSection>
    </div>
  );
};

export default ServiceFees;
